import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { QRCodeCanvas } from "qrcode.react";
import { api, formatApiError, API } from "../lib/api";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Switch } from "../components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogTrigger,
} from "../components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  ArrowLeft,
  ScanLine,
  Copy,
  Check,
  Calendar,
  MapPin,
  Users,
  Pencil,
  Download,
  UserPlus,
  Activity,
  CheckCircle2,
  Settings,
  Banknote,
  Trash2,
} from "lucide-react";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogFooter,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogAction,
  AlertDialogCancel,
} from "../components/ui/alert-dialog";

function LiveStats({ stats }) {
  if (!stats) return null;
  return (
    <div
      className="grid grid-cols-2 sm:grid-cols-4 gap-px bg-zinc-200 border border-zinc-200 rounded-md mb-10 overflow-hidden"
      data-testid="live-stats-row"
    >
      <StatCell
        label="Registered"
        value={`${stats.registered} / ${stats.event.capacity}`}
        testid="stat-registered"
      />
      <StatCell
        label="Checked-in"
        value={stats.checked_in}
        accent="text-green-700"
        testid="stat-checked-in"
      />
      <StatCell
        label="Pending check-in"
        value={stats.pending_checkin}
        testid="stat-pending-checkin"
      />
      <StatCell
        label="Remaining seats"
        value={stats.remaining}
        testid="stat-remaining"
      />
    </div>
  );
}

function StatCell({ label, value, accent, testid }) {
  return (
    <div className="bg-white p-5">
      <p className="overline mb-2">{label}</p>
      <p
        className={`font-heading text-2xl font-semibold tracking-tight ${
          accent || ""
        }`}
        data-testid={testid}
      >
        {value}
      </p>
    </div>
  );
}

export default function EventDetail() {
  const { eventId } = useParams();
  const [event, setEvent] = useState(null);
  const [regs, setRegs] = useState([]);
  const [stats, setStats] = useState(null);
  const [copied, setCopied] = useState(false);
  const [editing, setEditing] = useState(false);
  const [capacity, setCapacity] = useState("");
  const [addOpen, setAddOpen] = useState(false);
  const [addForm, setAddForm] = useState({
    name: "",
    sponsor_name: "",
    user_id_optional: "",
    first_time: false,
    mark_present: false,
  });
  const [addLoading, setAddLoading] = useState(false);

  const [editOpen, setEditOpen] = useState(false);
  const [editForm, setEditForm] = useState({
    name: "",
    date: "",
    location: "",
    price: "",
  });
  const [editLoading, setEditLoading] = useState(false);

  const [editRegOpen, setEditRegOpen] = useState(false);
  const [editRegId, setEditRegId] = useState(null);
  const [editRegForm, setEditRegForm] = useState({
    sponsor_name: "",
    email: "",
    phone: "",
    user_id_optional: "",
    first_time: false,
  });
  const [editRegLoading, setEditRegLoading] = useState(false);
  const [markPaidLoadingId, setMarkPaidLoadingId] = useState(null);
  const [deleteRegTarget, setDeleteRegTarget] = useState(null);
  const [deleteRegLoading, setDeleteRegLoading] = useState(false);

  const load = async () => {
    try {
      const [ev, rs, st] = await Promise.all([
        api.get(`/events/${eventId}`),
        api.get(`/events/${eventId}/registrations`),
        api.get(`/events/${eventId}/live-stats`),
      ]);
      setEvent(ev.data);
      setRegs(rs.data);
      setStats(st.data);
      setCapacity(String(ev.data.capacity));
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventId]);

  const publicUrl = `${window.location.origin}/e/${eventId}`;

  const copy = async () => {
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(publicUrl);
      } else {
        throw new Error("clipboard unavailable");
      }
      setCopied(true);
      toast.success("Link copied");
      setTimeout(() => setCopied(false), 1500);
    } catch {
      try {
        const tmp = document.createElement("textarea");
        tmp.value = publicUrl;
        tmp.setAttribute("readonly", "");
        tmp.style.position = "absolute";
        tmp.style.left = "-9999px";
        document.body.appendChild(tmp);
        tmp.select();
        document.execCommand("copy");
        document.body.removeChild(tmp);
        setCopied(true);
        toast.success("Link copied");
        setTimeout(() => setCopied(false), 1500);
      } catch {
        toast.error("Copy failed — please select the link manually");
      }
    }
  };

  const downloadQr = () => {
    const canvas = document.getElementById("registration-qr-large");
    if (!canvas) {
      toast.error("QR not ready yet");
      return;
    }
    const link = document.createElement("a");
    const safeName = (event?.name || "event")
      .replace(/[^a-zA-Z0-9-_ ]/g, "")
      .trim()
      .replace(/\s+/g, "-");
    link.download = `${safeName}-registration-qr.png`;
    link.href = canvas.toDataURL("image/png");
    link.click();
    toast.success("QR downloaded");
  };

  const saveCapacity = async () => {
    try {
      await api.put(`/events/${eventId}`, {
        capacity: parseInt(capacity, 10),
      });
      toast.success("Capacity updated");
      setEditing(false);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const markPresent = async (regId) => {
    try {
      const { data } = await api.post(
        `/registrations/${regId}/mark-present`
      );
      if (data.status === "already_checked_in") {
        toast.warning("Already marked present");
      } else {
        toast.success(`Marked present: ${data.registration.name}`);
      }
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const openEditReg = (reg) => {
    setEditRegId(reg.id);
    setEditRegForm({
      sponsor_name: reg.sponsor_name || "",
      email: reg.email || "",
      // Stored phone is +91XXXXXXXXXX; edit just the local digits, like the
      // public registration form does.
      phone: (reg.phone || "").replace(/^\+91/, ""),
      user_id_optional: reg.user_id_optional || "",
      first_time: !!reg.first_time,
    });
    setEditRegOpen(true);
  };

  const saveEditReg = async (e) => {
    e.preventDefault();
    setEditRegLoading(true);
    try {
      await api.put(`/registrations/${editRegId}`, {
        sponsor_name: editRegForm.sponsor_name,
        email: editRegForm.email.trim() ? editRegForm.email.trim() : "",
        phone: "+91" + editRegForm.phone.trim(),
        user_id_optional: editRegForm.user_id_optional,
        first_time: editRegForm.first_time,
      });
      toast.success("Registration updated");
      setEditRegOpen(false);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setEditRegLoading(false);
    }
  };

  const markPaid = async (regId) => {
    setMarkPaidLoadingId(regId);
    try {
      await api.post(`/registrations/${regId}/mark-paid`);
      toast.success("Marked as paid");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setMarkPaidLoadingId(null);
    }
  };

  const deleteReg = async () => {
    if (!deleteRegTarget) return;
    setDeleteRegLoading(true);
    try {
      await api.delete(`/registrations/${deleteRegTarget.id}`);
      toast.success("Registration removed");
      setDeleteRegTarget(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setDeleteRegLoading(false);
    }
  };

  const downloadCsv = async () => {
    try {
      const res = await fetch(`${API}/events/${eventId}/export.csv`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("organizer_token")}`,
        },
      });
      if (!res.ok) throw new Error(await res.text());
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${event?.name || "attendees"}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success("CSV downloaded");
    } catch (err) {
      toast.error(err.message || "Export failed");
    }
  };

  const addManual = async (e) => {
    e.preventDefault();
    setAddLoading(true);
    try {
      await api.post(`/events/${eventId}/add-manual`, addForm);
      toast.success("Participant added");
      setAddOpen(false);
      setAddForm({
        name: "",
        sponsor_name: "",
        user_id_optional: "",
        first_time: false,
        mark_present: false,
      });
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setAddLoading(false);
    }
  };

  const openEdit = () => {
    // Convert stored ISO date → datetime-local input value (yyyy-MM-ddTHH:mm)
    const d = new Date(event.date);
    const pad = (n) => String(n).padStart(2, "0");
    const localStr = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(
      d.getDate()
    )}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
    setEditForm({
      name: event.name,
      date: localStr,
      location: event.location || "",
      price: String(event.price),
    });
    setEditOpen(true);
  };

  const saveEdit = async (e) => {
    e.preventDefault();
    setEditLoading(true);
    try {
      const payload = {
        name: editForm.name,
        date: new Date(editForm.date).toISOString(),
        location: editForm.location,
        price: parseFloat(editForm.price),
      };
      await api.put(`/events/${eventId}`, payload);
      toast.success("Event updated");
      setEditOpen(false);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setEditLoading(false);
    }
  };

  if (!event)
    return (
      <div className="min-h-screen flex items-center justify-center text-zinc-500">
        Loading...
      </div>
    );

  const pct = Math.min(
    100,
    Math.round((event.registered / event.capacity) * 100)
  );
  const isFull = event.registered >= event.capacity;

  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      <nav className="bg-white border-b border-zinc-200">
        <div className="max-w-6xl mx-auto px-6 py-4">
          <Link
            to="/organizer/dashboard"
            className="inline-flex items-center gap-2 text-sm text-zinc-500 hover:text-black transition-colors"
            data-testid="back-to-dashboard"
          >
            <ArrowLeft className="w-4 h-4" /> Dashboard
          </Link>
        </div>
      </nav>

      <main className="max-w-6xl mx-auto px-6 py-10">
        <div className="flex flex-wrap items-start justify-between gap-4 mb-8">
          <div>
            <p className="overline mb-3">Event</p>
            <h1
              className="font-heading text-4xl sm:text-5xl font-semibold tracking-tighter"
              data-testid="event-title"
            >
              {event.name}
            </h1>
            <div className="mt-4 flex flex-wrap gap-5 text-sm text-zinc-600">
              <span className="flex items-center gap-2">
                <Calendar className="w-4 h-4" />
                {new Date(event.date).toLocaleString([], {
                  dateStyle: "medium",
                  timeStyle: "short",
                })}
              </span>
              {event.location && (
                <span className="flex items-center gap-2">
                  <MapPin className="w-4 h-4" />
                  {event.location}
                </span>
              )}
              <span className="flex items-center gap-2">
                <Users className="w-4 h-4" />
                {event.registered} / {event.capacity}
              </span>
              <span className="text-black font-medium">₹{event.price}</span>
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button
              variant="outline"
              onClick={openEdit}
              className="h-11 rounded-md border-zinc-200"
              data-testid="edit-event-button"
            >
              <Settings className="w-4 h-4 mr-1.5" />
              Edit event
            </Button>
            <Link to={`/organizer/events/${eventId}/live`}>
              <Button
                variant="outline"
                className="h-11 rounded-md border-zinc-200"
                data-testid="event-mode-button"
              >
                <Activity className="w-4 h-4 mr-1.5" />
                Event Mode
              </Button>
            </Link>
            <Link to={`/organizer/events/${eventId}/scan`}>
              <Button
                className="h-11 bg-black text-white hover:bg-zinc-800 rounded-md px-5"
                data-testid="open-scanner-button"
              >
                <ScanLine className="w-4 h-4 mr-1.5" />
                Scan QR
              </Button>
            </Link>
          </div>
        </div>

        {/* Edit Event dialog */}
        <Dialog open={editOpen} onOpenChange={setEditOpen}>
          <DialogContent className="sm:max-w-md rounded-md">
            <DialogHeader>
              <DialogTitle className="font-heading tracking-tight">
                Edit event
              </DialogTitle>
            </DialogHeader>
            <form
              onSubmit={saveEdit}
              className="space-y-4"
              data-testid="edit-event-form"
            >
              <div>
                <Label className="text-sm font-medium mb-1.5 block">
                  Event name
                </Label>
                <Input
                  required
                  value={editForm.name}
                  onChange={(e) =>
                    setEditForm({ ...editForm, name: e.target.value })
                  }
                  className="h-10 rounded-md border-zinc-200"
                  data-testid="edit-name-input"
                />
              </div>
              <div>
                <Label className="text-sm font-medium mb-1.5 block">
                  Date & time
                </Label>
                <Input
                  type="datetime-local"
                  required
                  value={editForm.date}
                  onChange={(e) =>
                    setEditForm({ ...editForm, date: e.target.value })
                  }
                  className="h-10 rounded-md border-zinc-200"
                  data-testid="edit-date-input"
                />
              </div>
              <div>
                <Label className="text-sm font-medium mb-1.5 block">
                  Location{" "}
                  <span className="text-zinc-400 font-normal">(optional)</span>
                </Label>
                <Input
                  value={editForm.location}
                  onChange={(e) =>
                    setEditForm({ ...editForm, location: e.target.value })
                  }
                  className="h-10 rounded-md border-zinc-200"
                  data-testid="edit-location-input"
                />
              </div>
              <div>
                <Label className="text-sm font-medium mb-1.5 block">
                  Price (₹)
                </Label>
                <Input
                  type="number"
                  min="100"
                  step="0.01"
                  required
                  value={editForm.price}
                  onChange={(e) =>
                    setEditForm({ ...editForm, price: e.target.value })
                  }
                  className="h-10 rounded-md border-zinc-200"
                  data-testid="edit-price-input"
                />
                <p className="mt-1.5 text-xs text-zinc-500">
                  Minimum ticket price is ₹100.
                </p>
                <p className="mt-1 text-xs text-zinc-500">
                  Existing registrations keep their paid amount. Price change
                  only affects new registrations.
                </p>
              </div>
              <p className="text-xs text-zinc-500">
                Tip: adjust capacity from the panel below (registrations
                auto-reopen when the cap is raised).
              </p>
              <DialogFooter>
                <Button
                  type="submit"
                  disabled={editLoading}
                  className="h-10 bg-black text-white hover:bg-zinc-800 rounded-md"
                  data-testid="edit-save-button"
                >
                  {editLoading ? "Saving..." : "Save changes"}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Edit Registration dialog */}
        <Dialog open={editRegOpen} onOpenChange={setEditRegOpen}>
          <DialogContent className="sm:max-w-md rounded-md">
            <DialogHeader>
              <DialogTitle className="font-heading tracking-tight">
                Edit registration
              </DialogTitle>
            </DialogHeader>
            <form
              onSubmit={saveEditReg}
              className="space-y-4"
              data-testid="edit-reg-form"
            >
              <div>
                <Label className="text-sm font-medium mb-1.5 block">
                  Sponsor name
                </Label>
                <Input
                  required
                  value={editRegForm.sponsor_name}
                  onChange={(e) =>
                    setEditRegForm({
                      ...editRegForm,
                      sponsor_name: e.target.value,
                    })
                  }
                  className="h-10 rounded-md border-zinc-200"
                  data-testid="edit-reg-sponsor-input"
                />
              </div>
              <div>
                <Label className="text-sm font-medium mb-1.5 block">
                  Email{" "}
                  <span className="text-zinc-400 font-normal">(optional)</span>
                </Label>
                <Input
                  type="email"
                  value={editRegForm.email}
                  onChange={(e) =>
                    setEditRegForm({ ...editRegForm, email: e.target.value })
                  }
                  className="h-10 rounded-md border-zinc-200"
                  data-testid="edit-reg-email-input"
                />
              </div>
              <div>
                <Label className="text-sm font-medium mb-1.5 block">
                  Phone number
                </Label>
                <div className="flex h-10 rounded-md border border-zinc-200 overflow-hidden focus-within:ring-2 focus-within:ring-black">
                  <span className="flex items-center px-3 bg-zinc-100 text-zinc-600 text-sm font-medium border-r border-zinc-200 select-none">
                    +91
                  </span>
                  <Input
                    type="tel"
                    required
                    value={editRegForm.phone}
                    onChange={(e) =>
                      setEditRegForm({
                        ...editRegForm,
                        phone: e.target.value.replace(/\D/g, "").slice(0, 10),
                      })
                    }
                    className="h-full border-0 rounded-none focus-visible:ring-0 focus-visible:ring-offset-0"
                    data-testid="edit-reg-phone-input"
                  />
                </div>
              </div>
              <div>
                <Label className="text-sm font-medium mb-1.5 block">
                  ID{" "}
                  <span className="text-zinc-400 font-normal">(optional)</span>
                </Label>
                <Input
                  value={editRegForm.user_id_optional}
                  onChange={(e) =>
                    setEditRegForm({
                      ...editRegForm,
                      user_id_optional: e.target.value,
                    })
                  }
                  className="h-10 rounded-md border-zinc-200"
                  data-testid="edit-reg-id-input"
                />
              </div>
              <div className="flex items-center justify-between p-3 border border-zinc-200 rounded-md">
                <span className="text-sm font-medium">First-time attendee?</span>
                <Switch
                  checked={editRegForm.first_time}
                  onCheckedChange={(v) =>
                    setEditRegForm({ ...editRegForm, first_time: v })
                  }
                  data-testid="edit-reg-firsttime-switch"
                />
              </div>
              <p className="text-xs text-zinc-500">
                Name and ticket count can't be changed here.
              </p>
              <DialogFooter>
                <Button
                  type="submit"
                  disabled={editRegLoading}
                  className="h-10 bg-black text-white hover:bg-zinc-800 rounded-md"
                  data-testid="edit-reg-save-button"
                >
                  {editRegLoading ? "Saving..." : "Save changes"}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Remove registration confirmation */}
        <AlertDialog
          open={!!deleteRegTarget}
          onOpenChange={(open) => !open && setDeleteRegTarget(null)}
        >
          <AlertDialogContent data-testid="delete-reg-dialog">
            <AlertDialogHeader>
              <AlertDialogTitle className="font-heading tracking-tight">
                Remove this registration?
              </AlertDialogTitle>
              <AlertDialogDescription>
                {deleteRegTarget?.name}'s unpaid registration (
                {Math.max(1, deleteRegTarget?.ticket_count || 1)} ticket
                {Math.max(1, deleteRegTarget?.ticket_count || 1) > 1 ? "s" : ""}
                ) will be permanently deleted. This can't be undone, and if they
                are still mid-payment they'll need to register again.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel
                disabled={deleteRegLoading}
                className="rounded-md"
                data-testid="delete-reg-cancel"
              >
                Cancel
              </AlertDialogCancel>
              <AlertDialogAction
                onClick={(e) => {
                  e.preventDefault();
                  deleteReg();
                }}
                disabled={deleteRegLoading}
                className="rounded-md bg-red-600 text-white hover:bg-red-700"
                data-testid="delete-reg-confirm"
              >
                {deleteRegLoading ? "Removing..." : "Remove"}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Live stats */}
        <LiveStats stats={stats} />

        {/* Public link + capacity editor */}
        <div className="grid md:grid-cols-2 gap-px bg-zinc-200 border border-zinc-200 rounded-md mb-10 overflow-hidden">
          <div className="bg-white p-6">
            <p className="overline mb-3">Public registration link</p>
            <div className="flex gap-2">
              <Input
                readOnly
                value={publicUrl}
                className="h-10 rounded-md border-zinc-200 font-mono text-xs"
                data-testid="public-link-input"
              />
              <Button
                variant="outline"
                onClick={copy}
                className="h-10 rounded-md border-zinc-200"
                data-testid="copy-link-button"
              >
                {copied ? (
                  <Check className="w-4 h-4" />
                ) : (
                  <Copy className="w-4 h-4" />
                )}
              </Button>
            </div>
            <div className="mt-4 flex items-center gap-4">
              <div
                className="p-2 border border-zinc-200 rounded-md bg-white shrink-0"
                data-testid="registration-qr"
              >
                <QRCodeCanvas value={publicUrl} size={96} marginSize={1} />
              </div>
              <div>
                <p className="text-sm text-zinc-600 mb-2">
                  Share this QR — scanning it opens the registration page.
                  Print it on posters or share on WhatsApp.
                </p>
                <Button
                  variant="outline"
                  onClick={downloadQr}
                  className="h-9 rounded-md border-zinc-200"
                  data-testid="download-qr-button"
                >
                  <Download className="w-4 h-4 mr-1.5" />
                  Download QR
                </Button>
              </div>
              {/* Hidden high-resolution canvas used for the PNG download */}
              <div className="hidden">
                <QRCodeCanvas
                  id="registration-qr-large"
                  value={publicUrl}
                  size={1024}
                  marginSize={4}
                />
              </div>
            </div>
            {isFull && (
              <p
                className="mt-3 text-sm text-red-600 font-medium"
                data-testid="capacity-full-notice"
              >
                Registrations Closed (Event Full) · raise the cap to reopen
              </p>
            )}
          </div>

          <div className="bg-white p-6">
            <div className="flex items-center justify-between mb-3">
              <p className="overline">Capacity</p>
              {!editing && (
                <button
                  onClick={() => setEditing(true)}
                  className="text-xs text-zinc-500 hover:text-black inline-flex items-center gap-1"
                  data-testid="edit-capacity-button"
                >
                  <Pencil className="w-3 h-3" />
                  Edit
                </button>
              )}
            </div>
            {editing ? (
              <div className="flex gap-2">
                <Input
                  type="number"
                  value={capacity}
                  onChange={(e) => setCapacity(e.target.value)}
                  className="h-10 rounded-md border-zinc-200"
                  data-testid="edit-capacity-input"
                />
                <Button
                  onClick={saveCapacity}
                  className="h-10 bg-black text-white hover:bg-zinc-800 rounded-md"
                  data-testid="save-capacity-button"
                >
                  Save
                </Button>
                <Button
                  variant="outline"
                  onClick={() => {
                    setEditing(false);
                    setCapacity(String(event.capacity));
                  }}
                  className="h-10 rounded-md border-zinc-200"
                >
                  Cancel
                </Button>
              </div>
            ) : (
              <>
                <p className="font-heading text-2xl font-semibold tracking-tight mb-3">
                  {event.registered} / {event.capacity}
                </p>
                <div className="w-full bg-zinc-100 h-2 rounded-sm overflow-hidden">
                  <div
                    className={`h-full ${
                      pct >= 100
                        ? "bg-red-600"
                        : pct >= 80
                        ? "bg-orange-500"
                        : "bg-black"
                    }`}
                    style={{ width: `${pct}%` }}
                  />
                </div>
                <p className="mt-2 text-xs text-zinc-500">{pct}% filled</p>
              </>
            )}
          </div>
        </div>

        {/* Registrations header + actions */}
        <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
          <p className="overline">Registrations</p>
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={downloadCsv}
              className="h-9 rounded-md border-zinc-200"
              data-testid="export-csv-button"
            >
              <Download className="w-4 h-4 mr-1.5" />
              Download CSV
            </Button>
            <Dialog open={addOpen} onOpenChange={setAddOpen}>
              <DialogTrigger asChild>
                <Button
                  className="h-9 bg-black text-white hover:bg-zinc-800 rounded-md"
                  data-testid="add-participant-button"
                >
                  <UserPlus className="w-4 h-4 mr-1.5" />
                  Add participant
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-md rounded-md">
                <DialogHeader>
                  <DialogTitle className="font-heading tracking-tight">
                    Add participant manually
                  </DialogTitle>
                </DialogHeader>
                <form
                  onSubmit={addManual}
                  className="space-y-4"
                  data-testid="add-manual-form"
                >
                  <div>
                    <Label className="text-sm font-medium mb-1.5 block">
                      Name
                    </Label>
                    <Input
                      required
                      value={addForm.name}
                      onChange={(e) =>
                        setAddForm({ ...addForm, name: e.target.value })
                      }
                      className="h-10 rounded-md border-zinc-200"
                      data-testid="manual-name-input"
                    />
                  </div>
                  <div>
                    <Label className="text-sm font-medium mb-1.5 block">
                      Sponsor
                    </Label>
                    <Input
                      required
                      value={addForm.sponsor_name}
                      onChange={(e) =>
                        setAddForm({ ...addForm, sponsor_name: e.target.value })
                      }
                      className="h-10 rounded-md border-zinc-200"
                      data-testid="manual-sponsor-input"
                    />
                  </div>
                  <div>
                    <Label className="text-sm font-medium mb-1.5 block">
                      ID{" "}
                      <span className="text-zinc-400 font-normal">
                        (optional)
                      </span>
                    </Label>
                    <Input
                      value={addForm.user_id_optional}
                      onChange={(e) =>
                        setAddForm({
                          ...addForm,
                          user_id_optional: e.target.value,
                        })
                      }
                      className="h-10 rounded-md border-zinc-200"
                      data-testid="manual-id-input"
                    />
                  </div>
                  <div className="flex items-center justify-between p-3 border border-zinc-200 rounded-md">
                    <span className="text-sm font-medium">
                      First-time attendee?
                    </span>
                    <Switch
                      checked={addForm.first_time}
                      onCheckedChange={(v) =>
                        setAddForm({ ...addForm, first_time: v })
                      }
                      data-testid="manual-firsttime-switch"
                    />
                  </div>
                  <div className="flex items-center justify-between p-3 border border-zinc-200 rounded-md">
                    <span className="text-sm font-medium">
                      Mark present immediately
                    </span>
                    <Switch
                      checked={addForm.mark_present}
                      onCheckedChange={(v) =>
                        setAddForm({ ...addForm, mark_present: v })
                      }
                      data-testid="manual-present-switch"
                    />
                  </div>
                  <DialogFooter>
                    <Button
                      type="submit"
                      disabled={addLoading}
                      className="h-10 bg-black text-white hover:bg-zinc-800 rounded-md"
                      data-testid="manual-submit-button"
                    >
                      {addLoading ? "Adding..." : "Add participant"}
                    </Button>
                  </DialogFooter>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        <div className="bg-white border border-zinc-200 rounded-md overflow-hidden">
          <Table data-testid="registrations-table">
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Sponsor</TableHead>
                <TableHead className="hidden sm:table-cell">Tickets</TableHead>
                <TableHead className="hidden sm:table-cell">ID</TableHead>
                <TableHead className="hidden sm:table-cell">First-time</TableHead>
                <TableHead>Payment</TableHead>
                <TableHead>Attendance</TableHead>
                <TableHead className="text-right">Action</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {regs.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={8}
                    className="text-center py-12 text-zinc-400"
                  >
                    No registrations yet. Share the public link.
                  </TableCell>
                </TableRow>
              ) : (
                regs.map((r) => {
                  const ticketCount = Math.max(1, r.ticket_count || 1);
                  const checkedInCount = Math.max(0, Math.min(ticketCount, r.checked_in_count || 0));
                  return (
                  <TableRow key={r.id} data-testid={`reg-row-${r.id}`}>
                    <TableCell className="font-medium">{r.name}</TableCell>
                    <TableCell>{r.sponsor_name}</TableCell>
                    <TableCell className="hidden sm:table-cell text-zinc-700">
                      {ticketCount}
                    </TableCell>
                    <TableCell className="hidden sm:table-cell text-zinc-500">
                      {r.user_id_optional || "—"}
                    </TableCell>
                    <TableCell className="hidden sm:table-cell text-zinc-500">
                      {r.first_time ? "Yes" : "No"}
                    </TableCell>
                    <TableCell>
                      {r.payment_status === "paid" ? (
                        <Badge className="bg-green-100 text-green-800 hover:bg-green-100 border-0 rounded-sm">
                          Paid
                          {r.payment_mode === "manual" ? " · manual" : ""}
                        </Badge>
                      ) : (
                        <Badge className="bg-zinc-100 text-zinc-600 hover:bg-zinc-100 border-0 rounded-sm">
                          Pending
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      {checkedInCount >= ticketCount ? (
                        <Badge className="bg-blue-100 text-blue-800 hover:bg-blue-100 border-0 rounded-sm">
                          Checked-in ({checkedInCount}/{ticketCount})
                        </Badge>
                      ) : checkedInCount > 0 ? (
                        <Badge className="bg-amber-100 text-amber-800 hover:bg-amber-100 border-0 rounded-sm">
                          Partial ({checkedInCount}/{ticketCount})
                        </Badge>
                      ) : (
                        <Badge className="bg-zinc-100 text-zinc-500 hover:bg-zinc-100 border-0 rounded-sm">
                          Absent
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="inline-flex items-center gap-2">
                        {r.payment_status === "paid" ? (
                          <>
                            <span className="text-xs text-zinc-500">
                              {checkedInCount}/{ticketCount}
                            </span>
                            {checkedInCount < ticketCount ? (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => markPresent(r.id)}
                                className="h-8 rounded-md border-zinc-200"
                                data-testid={`mark-present-${r.id}`}
                              >
                                <CheckCircle2 className="w-3.5 h-3.5 mr-1" />
                                Check in 1
                              </Button>
                            ) : (
                              <span className="text-xs text-zinc-400">Complete</span>
                            )}
                          </>
                        ) : (
                          <>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => markPaid(r.id)}
                              disabled={markPaidLoadingId === r.id}
                              className="h-8 rounded-md border-zinc-200"
                              data-testid={`mark-paid-${r.id}`}
                            >
                              <Banknote className="w-3.5 h-3.5 mr-1" />
                              {markPaidLoadingId === r.id
                                ? "Marking..."
                                : "Mark as paid"}
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => setDeleteRegTarget(r)}
                              className="h-8 w-8 p-0 rounded-md border-zinc-200 text-red-600 hover:text-red-700 hover:bg-red-50"
                              data-testid={`delete-reg-${r.id}`}
                              aria-label="Remove registration"
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </Button>
                          </>
                        )}
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => openEditReg(r)}
                          className="h-8 w-8 p-0 rounded-md border-zinc-200"
                          data-testid={`edit-reg-${r.id}`}
                          aria-label="Edit registration"
                        >
                          <Pencil className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </div>
      </main>
    </div>
  );
}
